import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Pencil, Send, Link2, ExternalLink, ArrowRightLeft, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { PageHeader, Panel, StatusBadge, Loading, Field } from "@/components/common";
import DocView from "@/components/DocView";
import { api, errMsg } from "@/lib/api";
import { fmtDay, STATUS_LABELS } from "@/lib/format";

export function deliveryToast(d) {
  if (!d) return;
  const msg = `Email ${STATUS_LABELS[d.status] || d.status}${d.error ? ` — ${d.error}` : ""}`;
  d.status === "sent" ? toast.success(msg) : toast.warning(msg);
}

function ConvertDialog({ open, onOpenChange, est }) {
  const navigate = useNavigate();
  const [inv, setInv] = useState(true);
  const [start, setStart] = useState("");
  const [dur, setDur] = useState(120);
  const [busy, setBusy] = useState(false);
  const go = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/estimates/${est.id}/convert`, { create_invoice: inv, schedule_start_local: start || null, duration_minutes: Number(dur) });
      toast.success("Estimate converted");
      if (data.conflicts?.length || data.availability_warnings?.length) toast.warning("Job scheduled, but it overlaps another job or falls outside your availability.");
      navigate(data.invoice_id ? `/invoices/${data.invoice_id}` : `/jobs/${data.appointment_id}`);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="convert-dialog">
        <DialogHeader><DialogTitle className="font-display">Convert to job & invoice</DialogTitle></DialogHeader>
        <label className="flex items-center gap-3 min-h-[44px]"><Checkbox checked={inv} onCheckedChange={(v) => setInv(!!v)} data-testid="convert-invoice-checkbox" />Create a draft invoice</label>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Schedule job (optional)" htmlFor="cv-start"><Input id="cv-start" type="datetime-local" value={start} onChange={(e) => setStart(e.target.value)} className="h-11" data-testid="convert-start-input" /></Field>
          <Field label="Duration (min)" htmlFor="cv-dur"><Input id="cv-dur" type="number" min={15} value={dur} onChange={(e) => setDur(e.target.value)} className="h-11" data-testid="convert-duration-input" /></Field>
        </div>
        <DialogFooter><Button disabled={busy || (!inv && !start)} onClick={go} data-testid="convert-submit-btn">{busy && <Loader2 className="h-4 w-4 animate-spin mr-2" />}Convert</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function EstimateDetail() {
  const { id } = useParams();
  const [est, setEst] = useState(null);
  const [busy, setBusy] = useState(false);
  const [convert, setConvert] = useState(false);

  const load = useCallback(() => {
    api.get(`/estimates/${id}`).then((r) => setEst(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [id]);

  useEffect(() => {
    load();
  }, [load]);

  const send = async (email) => {
    setBusy(true);
    try {
      const { data } = await api.post(`/estimates/${id}/send`, { email });
      if (email) deliveryToast(data.delivery);
      else { await navigator.clipboard?.writeText(data.link).catch(() => {}); toast.success("Approval link copied"); }
      load();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  if (!est) return <Loading />;
  const editable = ["draft", "sent", "declined"].includes(est.status);

  return (
    <div data-testid="estimate-detail-page">
      <Link to="/estimates" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900 mb-4"><ArrowLeft className="h-4 w-4" />Estimates</Link>
      <PageHeader title={`Estimate ${est.number}`} subtitle={`${est.client_name} · Created ${fmtDay(est.created_at)}${est.valid_until ? ` · Valid until ${fmtDay(est.valid_until)}` : ""}`} actions={<StatusBadge status={est.status} testId="estimate-status" />} />
      <div className="flex flex-wrap gap-2 mb-6">
        {editable && <Button variant="outline" asChild className="h-11"><Link to={`/estimates/${id}/edit`} data-testid="edit-estimate-btn"><Pencil className="h-4 w-4 mr-1" />Edit</Link></Button>}
        {editable && <Button className="h-11" disabled={busy} onClick={() => send(true)} data-testid="send-estimate-btn"><Send className="h-4 w-4 mr-1" />Send by email</Button>}
        {editable && <Button variant="outline" className="h-11" disabled={busy} onClick={() => send(false)} data-testid="copy-estimate-link-btn"><Link2 className="h-4 w-4 mr-1" />Copy approval link</Button>}
        {est.status !== "draft" && <Button variant="outline" asChild className="h-11"><a href={`/e/${est.public_token}`} target="_blank" rel="noreferrer" data-testid="view-public-estimate-btn"><ExternalLink className="h-4 w-4 mr-1" />Client view</a></Button>}
        {est.status === "approved" && <Button className="h-11" onClick={() => setConvert(true)} data-testid="convert-estimate-btn"><ArrowRightLeft className="h-4 w-4 mr-1" />Convert to job & invoice</Button>}
        {est.invoice_id && <Button variant="secondary" asChild className="h-11"><Link to={`/invoices/${est.invoice_id}`} data-testid="estimate-invoice-link">View invoice</Link></Button>}
        {est.appointment_id && <Button variant="secondary" asChild className="h-11"><Link to={`/jobs/${est.appointment_id}`} data-testid="estimate-job-link">View job</Link></Button>}
      </div>
      {est.responded_at && (
        <div className={`rounded-lg border p-4 mb-4 text-sm ${est.status === "declined" ? "border-red-200 bg-red-50 text-red-900" : "border-green-200 bg-green-50 text-green-900"}`} data-testid="estimate-response">
          {est.status === "declined" ? "Declined" : "Approved"} by {est.responder_name} on {new Date(est.responded_at).toLocaleString()}{est.response_comment ? ` — “${est.response_comment}”` : ""}
        </div>
      )}
      <Panel><DocView doc={est} /></Panel>
      <ConvertDialog open={convert} onOpenChange={setConvert} est={est} />
    </div>
  );
}
